/**
 * 教师端 - 我的积分页面
 * 
 * 功能:
 * 1. 积分概览: 总积分余额、可用积分、已过期积分、积分构成
 * 2. 积分明细: 单行紧凑布局、类型筛选、时间范围筛选、分页
 * 3. 赠送积分: 选择学生、输入数量、月度限额
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Gift, ChevronLeft, ChevronRight, Loader2, Users, Search, Tag, CheckCircle } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';

// ==================== 类型定义 ====================

interface StudentTagInfo {
  id: string;
  name: string;
  color: string;
}

interface Student {
  id: string;
  username: string;
  phone: string;
  tags: StudentTagInfo[];
}

interface StudentTag {
  id: string;
  name: string;
  color: string;
}

interface GiftLimit {
  gifted_points: number;
  gift_count: number;
  monthly_limit: number;
}

interface PointAccount {
  id: string;
  user_id: string;
  role: string;
  purchase_rebate_points: number;
  error_correction_points: number;
  activity_reward_points: number;
  teacher_gift_points: number;
  total_points: number;
  daily_earned_points: number;
}

interface Transaction {
  id: string;
  user_id: string;
  role: string;
  points_change: number;
  point_type: string;
  source_type: string;
  source_id: string | null;
  source_detail: any;
  balance_after: number;
  expire_at: string | null;
  operator_id: string | null;
  operator_role: string | null;
  remark: string | null;
  created_at: string;
}

// ==================== 常量 ====================

const POINT_TYPE_LABELS: Record<string, string> = {
  purchase_rebate: '购课返利',
  error_correction: '纠错奖励',
  activity_reward: '活动奖励',
  teacher_gift: '教师赠送',
  gift_deduction: '赠送扣减',
  deduction: '积分抵扣',
  expiration: '过期清零',
  refund_rollback: '退款回滚',
  admin_adjust: '管理员调整',
};

const POINT_TYPE_COLORS: Record<string, string> = {
  purchase_rebate: 'bg-blue-100 text-blue-700',
  error_correction: 'bg-green-100 text-green-700',
  activity_reward: 'bg-purple-100 text-purple-700',
  teacher_gift: 'bg-orange-100 text-orange-700',
  gift_deduction: 'bg-red-100 text-red-700',
  expiration: 'bg-gray-100 text-gray-700',
  refund_rollback: 'bg-yellow-100 text-yellow-700',
  admin_adjust: 'bg-indigo-100 text-indigo-700',
};

const TIME_RANGES = [
  { label: '全部', value: 'all' },
  { label: '近7天', value: '7d' },
  { label: '近30天', value: '30d' },
  { label: '近90天', value: '90d' },
];

const getStartDate = (range: string): string | undefined => {
  if (range === 'all') return undefined;
  const days: Record<string, number> = { '7d': 7, '30d': 30, '90d': 90 };
  const d = new Date();
  d.setDate(d.getDate() - days[range]);
  return d.toISOString().split('T')[0];
};

// ==================== 主组件 ====================

const TeacherGiftPoints: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userId, setUserId] = useState<string | null>(null);

  // Tab 切换
  const [activeTab, setActiveTab] = useState<'overview' | 'gift'>('overview');

  // 积分概览相关状态
  const [account, setAccount] = useState<PointAccount | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accountLoading, setAccountLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState<string>('all');
  const [timeRange, setTimeRange] = useState<string>('all');
  const pageSize = 20;

  // 赠送积分相关状态
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [displayedStudents, setDisplayedStudents] = useState<Student[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [points, setPoints] = useState('');
  const [remark, setRemark] = useState('');
  const [giftLimit, setGiftLimit] = useState<GiftLimit | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 标签筛选和搜索
  const [availableTags, setAvailableTags] = useState<StudentTag[]>([]);
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');

  // 初始化：获取教师 auth ID 和 session token
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }
      setUserId(user.id);

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || '';
      setAccessToken(token);

      // 数据加载
      fetchAccount(user.id, token);
      fetchTransactions(user.id, token);
      fetchStudents(user.id);
      fetchGiftLimit(user.id);

      const studentId = searchParams.get('studentId');
      if (studentId) {
        setSelectedStudentIds(prev => prev.includes(studentId) ? prev : [...prev, studentId]);
        setActiveTab('gift');
      }
    };
    init();
  }, []);

  // 筛选变化时获取流水
  useEffect(() => {
    if (userId && accessToken) {
      fetchTransactions(userId, accessToken);
    }
  }, [page, filterType, timeRange]);

  // ==================== 积分概览数据获取 ====================

  const fetchAccount = async (uid: string, token?: string) => {
    try {
      setAccountLoading(true);
      const { data: existingAccount } = await supabase
        .from('user_point_accounts')
        .select('*')
        .eq('user_id', uid)
        .eq('role', 'teacher')
        .maybeSingle();

      if (existingAccount) {
        setAccount(existingAccount);
      } else {
        // 创建教师积分账户
        const authToken = token || accessToken;
        const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            action: 'create_account',
            user_id: uid,
            role: 'teacher',
          }),
        });
        const result = await response.json();
        if (result.success) {
          setAccount(result.account);
        }
      }
    } catch (error) {
      console.error('Failed to fetch account:', error);
    } finally {
      setAccountLoading(false);
    }
  };

  const fetchTransactions = async (uid: string, token?: string) => {
    try {
      setTransactionsLoading(true);

      const body: any = {
        action: 'get_transactions',
        user_id: uid,
        role: 'teacher',
        page,
        page_size: pageSize,
      };

      if (filterType !== 'all') {
        body.point_type = filterType;
      }

      const startDate = getStartDate(timeRange);
      if (startDate) {
        body.start_date = startDate;
      }

      const authToken = token || accessToken;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();
      if (result.success) {
        setTransactions(result.transactions || []);
        setTotalPages(result.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  // ==================== 赠送积分数据获取 ====================

  const fetchStudents = async (uid: string) => {
    try {
      // 1. 获取教师绑定的学员列表
      const { data: teacherStudents } = await supabase
        .from('teacher_students')
        .select('student_id')
        .eq('teacher_id', uid);

      const studentIds = teacherStudents?.map(s => s.student_id) || [];

      if (studentIds.length === 0) {
        setAllStudents([]);
        setAvailableTags([]);
        return;
      }

      // 2. 获取学员基本信息
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, username, phone')
        .in('id', studentIds);

      // 3. 获取学员标签关系
      const { data: tagRelations } = await supabase
        .from('teacher_student_tag_relations')
        .select('student_id, tag_id, teacher_student_tags(id, name, color)')
        .in('student_id', studentIds);

      // 4. 组装学员数据（含标签）
      const tagMap: Record<string, StudentTagInfo[]> = {};
      tagRelations?.forEach((rel: any) => {
        if (!tagMap[rel.student_id]) tagMap[rel.student_id] = [];
        if (rel.teacher_student_tags) {
          tagMap[rel.student_id].push({
            id: rel.teacher_student_tags.id,
            name: rel.teacher_student_tags.name,
            color: rel.teacher_student_tags.color,
          });
        }
      });

      const studentList: Student[] = (profilesData || []).map((p: any) => ({
        id: p.id,
        username: p.username || '',
        phone: p.phone || '',
        tags: tagMap[p.id] || [],
      }));

      setAllStudents(studentList);

      // 5. 提取所有可用标签（去重）
      const tagSet = new Map<string, StudentTag>();
      studentList.forEach(s => {
        s.tags.forEach(t => {
          if (!tagSet.has(t.id)) {
            tagSet.set(t.id, { id: t.id, name: t.name, color: t.color });
          }
        });
      });
      setAvailableTags(Array.from(tagSet.values()));
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const fetchGiftLimit = async (uid: string) => {
    try {
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      const { data: limitData } = await supabase
        .from('teacher_gift_limits')
        .select('*')
        .eq('teacher_id', uid)
        .eq('month', currentMonth);

      const limitRecord = limitData && limitData.length > 0 ? limitData[0] : null;

      const { data: config } = await supabase
        .from('point_settings')
        .select('setting_value')
        .eq('setting_key', 'teacher_gift_monthly_limit');

      const configRecord = config && config.length > 0 ? config[0] : null;
      const monthlyLimit = (configRecord?.setting_value as any)?.max_points || 5000;

      setGiftLimit({
        gifted_points: limitRecord?.gifted_points || 0,
        gift_count: limitRecord?.gift_count || 0,
        monthly_limit: monthlyLimit,
      });
    } catch (error) {
      console.error('Failed to fetch gift limit:', error);
    }
  };

  // ==================== 学员筛选（基于所有数据） ====================

  const filteredAllStudents = useMemo(() => {
    return allStudents.filter(student => {
      if (selectedTagFilters.length > 0) {
        const hasMatchingTag = student.tags.some(tag => selectedTagFilters.includes(tag.id));
        if (!hasMatchingTag) return false;
      }
      if (studentSearchQuery.trim()) {
        const query = studentSearchQuery.trim().toLowerCase();
        const username = (student.username || '').toLowerCase();
        const phone = (student.phone || '').toLowerCase();
        const tagNames = student.tags.map(t => t.name.toLowerCase()).join(' ');
        return username.includes(query) || phone.includes(query) || tagNames.includes(query);
      }
      return true;
    });
  }, [allStudents, selectedTagFilters, studentSearchQuery]);

  useEffect(() => {
    setDisplayedStudents(filteredAllStudents);
  }, [filteredAllStudents]);

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds(prev =>
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    if (selectedStudentIds.length === filteredAllStudents.length) {
      const filteredIds = new Set(filteredAllStudents.map(s => s.id));
      setSelectedStudentIds(prev => prev.filter(id => !filteredIds.has(id)));
    } else {
      const filteredIds = filteredAllStudents.map(s => s.id);
      setSelectedStudentIds(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const toggleTagFilter = (tagId: string) => {
    setSelectedTagFilters(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  // ==================== 赠送积分提交 ====================

  const handleSubmit = async () => {
    if (!userId) return;
    if (selectedStudentIds.length === 0) {
      alert('请选择学生');
      return;
    }

    const pointsNum = parseInt(points);
    if (!pointsNum || pointsNum <= 0) {
      alert('请输入有效的积分数量');
      return;
    }

    const totalPoints = pointsNum * selectedStudentIds.length;
    if (giftLimit && giftLimit.gifted_points + totalPoints > giftLimit.monthly_limit) {
      alert(`超出月度限额: 已赠送${giftLimit.gifted_points}分, 本次需${totalPoints}分, 限额${giftLimit.monthly_limit}分`);
      return;
    }

    try {
      setSubmitting(true);
      let successCount = 0;
      let failCount = 0;

      for (const studentId of selectedStudentIds) {
        try {
          const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              action: 'teacher_gift',
              teacher_id: userId,
              student_id: studentId,
              points: pointsNum,
              remark: remark || undefined,
            }),
          });

          const result = await response.json();
          if (result.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }

      if (successCount > 0) {
        alert(`赠送完成: 成功${successCount}人${failCount > 0 ? `, 失败${failCount}人` : ''}`);
        fetchGiftLimit(userId);
        fetchAccount(userId);
        setPoints('');
        setRemark('');
        setSelectedStudentIds([]);
      } else {
        alert('赠送失败,请重试');
      }
    } catch (error) {
      console.error('Failed to gift points:', error);
      alert('赠送失败,请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 已过期积分 = 总积分 - 各类可用积分之和
  const availablePoints = (account?.purchase_rebate_points || 0) +
    (account?.error_correction_points || 0) +
    (account?.activity_reward_points || 0) +
    (account?.teacher_gift_points || 0);
  const expiredPoints = (account?.total_points || 0) - availablePoints;

  const remainingPoints = giftLimit ? giftLimit.monthly_limit - giftLimit.gifted_points : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Crown className="w-5 h-5 text-orange-500" />
          <h1 className="text-xl font-bold text-gray-800">我的积分</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Tab 切换 */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'overview'
                ? 'bg-orange-500 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <Crown className="w-4 h-4 inline mr-1" />
            积分概览
          </button>
          <button
            onClick={() => setActiveTab('gift')}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'gift'
                ? 'bg-blue-500 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <Gift className="w-4 h-4 inline mr-1" />
            赠送积分
          </button>
        </div>

        {/* ==================== Tab 1: 积分概览 ==================== */}
        {activeTab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* 总积分余额卡片 */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 text-white shadow-lg">
              <div className="text-center mb-4">
                <div className="text-sm opacity-90 mb-1">我的总积分</div>
                <div className="text-4xl font-bold">{account?.total_points || 0}</div>
              </div>
              <div className="flex items-center justify-center gap-6 text-sm">
                <div>
                  <span className="opacity-75">可用积分</span>
                  <span className="font-bold ml-2">{availablePoints}</span>
                </div>
                {expiredPoints > 0 && (
                  <div>
                    <span className="opacity-75">已过期</span>
                    <span className="font-bold ml-2">{expiredPoints}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 积分构成卡片 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">积分构成</h2>
              <div className="grid grid-cols-2 gap-3">
                <PointCategoryCard
                  title="购课返利"
                  points={account?.purchase_rebate_points || 0}
                  color="blue"
                  icon="rebate"
                />
                <PointCategoryCard
                  title="纠错奖励"
                  points={account?.error_correction_points || 0}
                  color="green"
                  icon="correction"
                />
                <PointCategoryCard
                  title="活动奖励"
                  points={account?.activity_reward_points || 0}
                  color="purple"
                  icon="reward"
                />
                <PointCategoryCard
                  title="教师赠送"
                  points={account?.teacher_gift_points || 0}
                  color="orange"
                  icon="gift"
                />
              </div>
            </div>

            {/* 筛选器 */}
            <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
              {/* 类型筛选 */}
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                <FilterChip label="全部" active={filterType === 'all'} onClick={() => { setFilterType('all'); setPage(1); }} />
                <FilterChip label="活动奖励" active={filterType === 'activity_reward'} onClick={() => { setFilterType('activity_reward'); setPage(1); }} />
                <FilterChip label="教师赠送" active={filterType === 'teacher_gift'} onClick={() => { setFilterType('teacher_gift'); setPage(1); }} />
                <FilterChip label="管理员调整" active={filterType === 'admin_adjust'} onClick={() => { setFilterType('admin_adjust'); setPage(1); }} />
                <FilterChip label="购课返利" active={filterType === 'purchase_rebate'} onClick={() => { setFilterType('purchase_rebate'); setPage(1); }} />
                <FilterChip label="积分抵扣" active={filterType === 'deduction'} onClick={() => { setFilterType('deduction'); setPage(1); }} />
                <FilterChip label="过期清零" active={filterType === 'expiration'} onClick={() => { setFilterType('expiration'); setPage(1); }} />
              </div>
              {/* 时间范围筛选 */}
              <div className="flex items-center gap-2 overflow-x-auto">
                {TIME_RANGES.map((range) => (
                  <FilterChip
                    key={range.value}
                    label={range.label}
                    active={timeRange === range.value}
                    onClick={() => { setTimeRange(range.value); setPage(1); }}
                    size="sm"
                  />
                ))}
              </div>
            </div>

            {/* 积分明细列表 */}
            {transactionsLoading && transactions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
                加载中...
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-2">
                  <Crown className="w-12 h-12 mx-auto" />
                </div>
                <div className="text-gray-500">暂无积分流水记录</div>
              </div>
            ) : (
              <div className="space-y-2">
                {transactions.map((tx) => (
                  <TransactionItem key={tx.id} transaction={tx} formatDate={formatDate} />
                ))}
              </div>
            )}

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  上一页
                </button>
                <span className="text-sm text-gray-600">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
                >
                  下一页
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* ==================== Tab 2: 赠送积分 ==================== */}
        {activeTab === 'gift' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* 月度限额卡片 */}
            {giftLimit && (
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-sm opacity-90">本月已赠送</div>
                    <div className="text-3xl font-bold">{giftLimit.gifted_points} / {giftLimit.monthly_limit}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm opacity-90">赠送次数</div>
                    <div className="text-2xl font-bold">{giftLimit.gift_count} 次</div>
                  </div>
                </div>
                <div className="bg-white/20 rounded-full h-2">
                  <div
                    className="bg-white h-2 rounded-full transition-all"
                    style={{ width: `${(giftLimit.gifted_points / giftLimit.monthly_limit) * 100}%` }}
                  />
                </div>
                <div className="text-xs mt-2 opacity-75">
                  剩余可赠送: {remainingPoints} 分
                </div>
              </div>
            )}

            {/* 学员选择区域 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-800">选择学员</h2>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full">
                  已选 {selectedStudentIds.length} 人
                </span>
              </div>

              {/* 标签筛选 */}
              {availableTags.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">按标签筛选（多选，OR逻辑）</span>
                    {selectedTagFilters.length > 0 && (
                      <button
                        onClick={() => setSelectedTagFilters([])}
                        className="text-xs text-gray-500 hover:text-red-600 ml-2"
                      >
                        清空筛选
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map(tag => {
                      const isSelected = selectedTagFilters.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => toggleTagFilter(tag.id)}
                          className={`px-3 py-1.5 text-sm rounded-full border-2 transition-all ${
                            isSelected
                              ? 'border-transparent text-white'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 bg-white'
                          }`}
                          style={isSelected ? { backgroundColor: tag.color, borderColor: tag.color } : {}}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 搜索框 */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜索学员姓名、手机号或标签"
                  value={studentSearchQuery}
                  onChange={(e) => setStudentSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>

              {/* 操作栏 */}
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-600">
                  {filteredAllStudents.length === 0 ? (
                    <span>未找到匹配的学员</span>
                  ) : (
                    <span>共 <span className="font-medium text-gray-900">{filteredAllStudents.length}</span> 位学员</span>
                  )}
                </div>
                {filteredAllStudents.length > 0 && (
                  <button
                    onClick={selectAllStudents}
                    className="text-sm text-purple-600 hover:text-purple-700"
                  >
                    {filteredAllStudents.every(s => selectedStudentIds.includes(s.id)) ? '取消全选' : '全选'}
                  </button>
                )}
              </div>

              {/* 学员列表 */}
              {allStudents.length > 0 ? (
                <div className="space-y-2 max-h-[250px] overflow-y-auto mb-4">
                  {displayedStudents.map(student => {
                    const isSelected = selectedStudentIds.includes(student.id);
                    return (
                      <div
                        key={student.id}
                        onClick={() => toggleStudentSelection(student.id)}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-gray-800 text-sm">{student.username}</p>
                            {student.tags.length > 0 && (
                              <div className="flex items-center gap-1">
                                {student.tags.map(tag => (
                                  <span
                                    key={tag.id}
                                    className="px-2 py-0.5 rounded-full text-xs text-white"
                                    style={{ backgroundColor: tag.color || '#6366f1' }}
                                  >
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{student.phone}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">暂无学员数据</div>
              )}
            </div>

            {/* 表单 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              {/* 积分数量 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  每人赠送积分 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  placeholder="请输入积分数量"
                  min="1"
                  max={remainingPoints}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="text-xs text-gray-500 mt-1">
                  已选 {selectedStudentIds.length} 人，总计需 {points ? parseInt(points) * selectedStudentIds.length : 0} 分 | 单次最多可赠送 {remainingPoints} 分
                </div>
              </div>

              {/* 备注 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  备注 (可选)
                </label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="例如: 学习进步奖励"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* 提交按钮 */}
              <button
                onClick={handleSubmit}
                disabled={submitting || selectedStudentIds.length === 0 || !points}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    赠送中...
                  </span>
                ) : `确认赠送 (${selectedStudentIds.length}人 × ${points || 0}分)`}
              </button>
            </div>

            {/* 说明 */}
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="text-sm text-blue-800">
                <div className="font-medium mb-2">赠送说明</div>
                <ul className="space-y-1 text-xs">
                  <li>• 赠送的积分有效期为1年</li>
                  <li>• 学生可在积分中心查看收到的积分</li>
                  <li>• 每月赠送上限为 {giftLimit?.monthly_limit || 5000} 分</li>
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ==================== 子组件 ====================

// 积分构成卡片
const PointCategoryCard: React.FC<{
  title: string;
  points: number;
  color: 'blue' | 'green' | 'purple' | 'orange';
  icon: 'rebate' | 'correction' | 'reward' | 'gift';
}> = ({ title, points, color, icon }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-purple-50 border-purple-200',
    orange: 'bg-orange-50 border-orange-200',
  };

  const iconClasses = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    purple: 'text-purple-500',
    orange: 'text-orange-500',
  };

  const iconMap = {
    rebate: '🎓',
    correction: '🔍',
    reward: '🎁',
    gift: '👨‍🏫',
  };

  return (
    <div className={`rounded-xl p-4 border ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-lg ${iconClasses[color]}`}>{iconMap[icon]}</span>
        <span className="text-sm font-medium text-gray-700">{title}</span>
      </div>
      <div className="text-2xl font-bold text-gray-800">{points}</div>
    </div>
  );
};

// 筛选芯片
const FilterChip: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  size?: 'sm' | 'md';
}> = ({ label, active, onClick, size = 'md' }) => {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
        size === 'sm' ? 'px-2.5 py-1 text-xs' : ''
      } ${
        active
          ? 'bg-orange-500 text-white'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
};

// 生成交易描述 - 详细记载积分动向的具体原因
const getTransactionDescription = (transaction: Transaction): string => {
  const { source_type, point_type, source_detail, remark, points_change } = transaction;
  const typeLabel = POINT_TYPE_LABELS[point_type] || point_type;
  const isPositive = points_change > 0;
  const absPoints = Math.abs(points_change);

  // 优先使用 remark 作为描述基础
  if (remark) {
    // admin_adjust: remark 就是原因
    if (source_type === 'admin_adjust') {
      return isPositive
        ? `管理员增加${typeLabel}${absPoints}分：${remark}`
        : `管理员扣减${typeLabel}${absPoints}分：${remark}`;
    }
    // gift_deduction: 教师赠送扣减
    if (source_type === 'gift_deduction') {
      const studentId = source_detail?.student_id;
      return remark.includes('扣减')
        ? remark
        : `赠送学员扣减${absPoints}分：${remark}`;
    }
    // gift: 教师赠送学员
    if (source_type === 'gift') {
      return remark.includes('教师') || remark.includes('赠送')
        ? remark
        : `教师赠送${absPoints}分：${remark}`;
    }
    // 其他类型直接用 remark
    return remark;
  }

  // 没有 remark 时，根据 source_type 和 source_detail 生成描述
  switch (source_type) {
    case 'admin_adjust':
      return isPositive
        ? `管理员增加${typeLabel}${absPoints}分`
        : `管理员扣减${typeLabel}${absPoints}分`;
    case 'gift_deduction':
      return `赠送学员扣减${absPoints}分`;
    case 'gift':
      return `教师赠送${absPoints}分`;
    case 'purchase_rebate':
      return `购课返利${absPoints}分`;
    case 'error_correction':
      return `纠错奖励${absPoints}分`;
    case 'order_deduction':
      return `积分抵扣订单${absPoints}分`;
    case 'refund_rollback':
      return `退款回滚${absPoints}分`;
    case 'expiration':
      return `积分过期清零${absPoints}分`;
    default:
      return isPositive
        ? `增加${typeLabel}${absPoints}分`
        : `扣减${typeLabel}${absPoints}分`;
  }
};

// 流水项组件 - 两行布局：上行描述+积分变动，下行时间+余额
const TransactionItem: React.FC<{
  transaction: Transaction;
  formatDate: (date: string) => string;
}> = ({ transaction, formatDate }) => {
  const isPositive = transaction.points_change > 0;
  const description = getTransactionDescription(transaction);

  return (
    <div className="bg-white rounded-xl px-4 py-3 shadow-sm">
      {/* 上行：类型标签 + 描述 + 积分变动 */}
      <div className="flex items-center gap-2">
        {/* 类型标签 */}
        <span
          className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
            POINT_TYPE_COLORS[transaction.point_type] || 'bg-gray-100 text-gray-700'
          }`}
        >
          {POINT_TYPE_LABELS[transaction.point_type] || transaction.point_type}
        </span>

        {/* 详细描述 */}
        <span className="text-sm text-gray-700 truncate">
          {description}
        </span>

        {/* 积分变动 */}
        <div className={`text-sm font-bold whitespace-nowrap ml-auto ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {isPositive ? '+' : ''}{transaction.points_change}
        </div>
      </div>

      {/* 下行：操作时间 + 余额 */}
      <div className="flex items-center gap-3 mt-1">
        <div className="text-xs text-gray-400 whitespace-nowrap">
          {formatDate(transaction.created_at)}
        </div>
        <div className="text-xs text-orange-500 font-medium whitespace-nowrap ml-auto">
          余额: {transaction.balance_after}
        </div>
      </div>
    </div>
  );
};

export default TeacherGiftPoints;